import type { ReactNode } from 'react';

import { EINSTELLUNGS_TEXTE } from '../../i18n/einstellungen.generiert';
import type { Locale } from '../../i18n';
import { EINTRAEGE, GRUPPEN, type KatalogEintrag } from '../../platform/katalog.generiert';
import type { Schluessel, Wert } from '../../platform/einstellungen';

/**
 * Die Einstellungen der App-Oberfläche — gezeichnet aus dem Katalog, nicht von Hand
 * aufgeschrieben.
 *
 * **Warum.** Bis zum 2026-09-17 führte `SettingsDialog.tsx` seine Zeilen selbst: jede
 * Einstellung stand dort noch einmal, mit eigenem i18n-Schlüssel, eigener Reihenfolge und
 * eigener Entscheidung darüber, ob sie erscheint. Das Feld `inOberflaeche` in
 * `settings/katalog.json` war damit eine Behauptung ohne Leser — der Nutzer konnte es setzen,
 * und nichts geschah. Jetzt entscheidet es wirklich.
 *
 * ⚠️ **Was hier gezeichnet wird und was nicht.** Generisch sind die Einträge, die genau einen
 * Wert setzen: Schalter und Auswahlen. Alles, was **mehr** tut, bleibt im Dialog von Hand —
 * die Sprachwahl (Flaggen, eigener Store), die Akzentfarbe (Farbflächen statt Beschriftungen),
 * der Gerätename (dazu die Kennung darunter) und der Serverzugang (Anmeldeprobe, Katalog
 * laden). Sie stehen als `SONDERFAELLE` unten und folgen demselben `inOberflaeche`.
 *
 * ⚠️ **Die Reihenfolge ist die des Katalogs**, Gruppen wie Einträge. Wer sie ändern will,
 * ändert die Quelle — nicht diese Datei.
 */

/** Was eine Einstellung gerade trägt und wie man sie ändert. */
export interface Bindung {
  wert: Wert;
  setze: (neu: Wert) => void;
}

/** Alle Bindungen, die der Dialog kennt. Fehlt eine, wird der Eintrag nicht gezeichnet. */
export type Bindungen = Partial<Record<Schluessel, Bindung>>;

/**
 * Einträge, die eine eigene Darstellung haben.
 *
 * ⚠️ **Sie werden hier nur *gemeldet*, nicht gezeichnet.** Der Dialog entscheidet, was er an
 * ihrer Stelle einsetzt; diese Liste sorgt dafür, dass die generische Schleife sie nicht
 * zusätzlich als Schalter oder Auswahl zeichnet.
 */
export const SONDERFAELLE: readonly Schluessel[] = [
  'language',
  'ui_accent',
  'device_name',
  'server_protocol',
  'server_url',
  'classic_login',
  'classic_password',
  // Das Passwort vor den Einstellungen hat ein eigenes Feld mit zweimaliger Eingabe
  // (`PasswortSetzen`, seit 2026-10-02 — Entscheidung des Nutzers).
  'settings_password',
];

/** Steht dieser Eintrag in der App-Oberfläche? */
export const inOberflaeche = (name: Schluessel): boolean =>
  EINTRAEGE.find((e) => e.name === name)?.inOberflaeche ?? false;

/** Die Einträge einer Gruppe, die generisch gezeichnet werden. */
export function eintraegeDerGruppe(gruppe: string): KatalogEintrag[] {
  return EINTRAEGE.filter(
    (e) => e.gruppe === gruppe && e.inOberflaeche && !SONDERFAELLE.includes(e.name as Schluessel),
  );
}

/** Die Gruppen in ihrer Reihenfolge. */
export const gruppenReihenfolge = (): string[] => GRUPPEN.map((g) => g.id);

/** Die Beschriftung einer Gruppe in der gewählten Sprache. */
export const gruppenText = (gruppe: string, locale: Locale): string =>
  EINSTELLUNGS_TEXTE[locale].gruppen[gruppe] ?? gruppe;

/** Die Beschreibung eines Eintrags — der Langtext hinter seinem „?". */
export const eintragsBeschreibung = (name: string, locale: Locale): string | undefined =>
  EINSTELLUNGS_TEXTE[locale].beschreibungen[name];

/** Die Beschriftung eines Eintrags. */
export const eintragsText = (name: string, locale: Locale): string =>
  EINSTELLUNGS_TEXTE[locale].eintraege[name] ?? name;

/** Die Beschriftung eines Auswahlwerts. */
export const wertText = (name: string, wert: Wert, locale: Locale): string =>
  EINSTELLUNGS_TEXTE[locale].werte[name]?.[String(wert)] ?? String(wert);

/**
 * Zeichnet einen Eintrag.
 *
 * ⚠️ **Ein Text- oder Passwortfeld gibt es hier nicht.** Die drei, die es in der App gäbe
 * (Gerätename, Serverzugang, Einstellungssperre), sind alle Sonderfälle mit eigenem Verhalten;
 * ein generisches Eingabefeld wäre eine Bauform ohne Benutzer. Kommt eines dazu, gehört es
 * hierher — bis dahin fällt der Eintrag lieber weg, als halb bedienbar dazustehen.
 */
export function zeichneEintrag(
  eintrag: KatalogEintrag,
  bindung: Bindung | undefined,
  locale: Locale,
  gesperrt: boolean,
  bausteine: {
    Schalter: (p: { label: string; on: boolean; onChange: (n: boolean) => void; gesperrt?: boolean }) => ReactNode;
    Stufen: (p: {
      label: string;
      werte: { wert: Wert; text: string }[];
      aktuell: Wert;
      onChange: (n: Wert) => void;
      gesperrt?: boolean;
    }) => ReactNode;
  },
): ReactNode {
  if (!bindung) return null;
  const label = eintragsText(eintrag.name, locale);

  if (eintrag.typ === 'schalter') {
    return bausteine.Schalter({
      label,
      on: bindung.wert === true,
      onChange: (n) => bindung.setze(n),
      gesperrt,
    });
  }

  if (eintrag.typ === 'auswahl' || eintrag.typ === 'auswahlZahl') {
    return bausteine.Stufen({
      label,
      werte: (eintrag.werte ?? []).map((w) => ({
        wert: w.wert,
        text: wertText(eintrag.name, w.wert, locale),
      })),
      aktuell: bindung.wert,
      onChange: (n) => bindung.setze(n),
      gesperrt,
    });
  }

  return null;
}
